import React, { useState, useRef } from 'react';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import { PricingPlan, OrderSummary, PaymentMethod } from '../types/pricing.types';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: PricingPlan;
  orderSummary: OrderSummary;
  paymentMethod: PaymentMethod;
  formatPrice: (price: number) => string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  plan,
  orderSummary,
  paymentMethod,
  formatPrice,
}) => {
  const { t, isRTL } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptUploaded, setReceiptUploaded] = useState(false);
  const [shebaCopied, setShebaCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shebaNumber = 'IR123456789012345678901234';

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      setReceiptUploaded(true);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCopySheba = async () => {
    try {
      await navigator.clipboard.writeText(shebaNumber);
      setShebaCopied(true);
      setTimeout(() => {
        setShebaCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = () => {
    // TODO: Implement payment submission logic
    console.log('Payment submitted:', {
      plan: plan.id,
      paymentMethod,
      receiptFile,
      orderSummary,
    });
    // Close modal after submission
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        className={`rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 ${
          isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {paymentMethod === 'online'
              ? t('pricing.orderSummary.onlinePayment')
              : t('pricing.orderSummary.shebaPayment')}
          </h2>
          <button
            onClick={onClose}
            className={`text-2xl font-bold ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ×
          </button>
        </div>

        {/* Order Summary */}
        <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t('pricing.orderSummary.plan')}:
              </span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{plan.name}</span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t('pricing.orderSummary.total')}:
              </span>
              <span className={`font-bold text-primary`}>
                {formatPrice(orderSummary.total)} {t('pricing.toman')}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Method Specific Content */}
        {paymentMethod === 'online' ? (
          <div className="mb-6">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {t('pricing.orderSummary.onlinePayment')} - درگاه پرداخت آنلاین
            </p>
            <button
              onClick={handleSubmit}
              className="w-full py-3 rounded-lg font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              پرداخت آنلاین
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {t('pricing.orderSummary.shebaPayment')} - لطفاً مبلغ را به شماره شبا واریز کنید و
              رسید پرداخت را آپلود کنید.
            </p>

            {/* Sheba Number Display */}
            <div
              className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}
            >
              <div className="flex justify-between items-center mb-2">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  شماره شبا:
                </p>
                <button
                  onClick={handleCopySheba}
                  className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors ${
                    shebaCopied
                      ? 'bg-green-500 text-white'
                      : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  title={shebaCopied ? 'کپی شد!' : 'کپی کردن شماره شبا'}
                >
                  {shebaCopied ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-xs">کپی شد!</span>
                    </>
                  ) : (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <p className={`font-mono text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {shebaNumber}
              </p>
            </div>

            {/* File Upload */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={handleUploadClick}
                className={`w-full py-3 rounded-lg font-semibold border-2 border-dashed transition-colors ${
                  receiptUploaded
                    ? 'border-green-500 text-green-500 bg-green-50 dark:bg-green-900/20'
                    : isDark
                    ? 'border-gray-600 text-gray-400 hover:border-gray-500'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {receiptUploaded ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {t('pricing.orderSummary.receiptUploaded')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg
                      className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    {t('pricing.orderSummary.selectFile')}
                  </span>
                )}
              </button>
              {receiptFile && (
                <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {receiptFile.name}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!receiptUploaded}
              className={`w-full py-3 rounded-lg font-semibold mt-4 transition-colors ${
                receiptUploaded
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              ثبت سفارش
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
